/** Creating a repository and attaching it to the Memory Service. */

import { request } from "./http";

export interface Sample {
  id: string;
  label: string;
  language: string;
  summary: string;
  file_count: number;
  paths: string[];
}

export interface ProvisionedRepository {
  full_name: string;
  /** The URL the Memory Service will clone — an in-network host, not a browser-reachable one. */
  clone_url: string;
  web_url: string;
  default_branch: string;
  files_written: string[];
}

export interface ProvisionResult {
  repository: ProvisionedRepository;
  attached: boolean;
  repository_id: string | null;
  job_id: string | null;
  poll_url: string | null;
  /** Id of the recorded call, so the ingestion job can be watched like any other. */
  call_id: string | null;
  /** Set when the repository exists but attaching did not happen or failed. */
  detail: string | null;
}

export interface RepositoryFile {
  path: string;
  content: string;
}

export interface ProvisionInput {
  name: string;
  sample?: string;
  files?: RepositoryFile[];
  attach?: boolean;
  description?: string;
}

export function fetchSamples(signal?: AbortSignal): Promise<Sample[]> {
  return request<Sample[]>("/api/samples", { ...(signal ? { signal } : {}) });
}

export function provisionRepository(input: ProvisionInput): Promise<ProvisionResult> {
  return request<ProvisionResult>("/api/repositories", { method: "POST", body: input });
}

/** Gitea's own health, reported separately because the Memory Service does not check it. */
export interface GiteaHealth {
  configured: boolean;
  reachable: boolean;
  status: string | null;
  version: string | null;
  can_provision: boolean;
  detail: string | null;
}

/** One repository the Memory Service is tracking. */
export interface AttachedRepository {
  id: string;
  full_name: string;
  provider: string | null;
  default_branch: string | null;
  status: string | null;
  /** Whether a push webhook was registered — the difference between a real and a simulated push. */
  webhook_registered: boolean;
  webhook_id: string | null;
  last_synced_commit: string | null;
  attached_at: string | null;
  files_indexed: number | null;
  /** Non-zero means indexing is still catching up, so code queries may come back thin. */
  files_pending: number | null;
}

export interface AttachedRepositoryList {
  items: AttachedRepository[];
  detail: string | null;
}

export function fetchAttachedRepositories(
  signal?: AbortSignal,
): Promise<AttachedRepositoryList> {
  return request<AttachedRepositoryList>("/api/repositories", {
    ...(signal ? { signal } : {}),
  });
}
